import { ErrorHandler, inject, Injectable } from "@angular/core";
import { API_REQUEST_METHODS, HttpService } from "../../../ancestors/base-api.service";
import { BackofficeProcess, BackofficeProcessConfigurationStatus, BackofficeProcessDetails, BackofficeProcessPhases, BackofficeProcessUpdateRequest, CompetenceImportTargetEnum, CreateProcessRequest, EvaluationCommentListResult, ManagerScorecardDetails, ManagerScorecardSummary, MonitoringSummary, MonitoringSummaryDevelopmentPlan, MonitoringUserDevelopmentPlanSummary, MonitoringUserFiltersValues, MonitoringUserSummary, MonitoringUserSummaryIds, Process, ProcessUser } from "alloy-performance-engine-cl";
import { AllUsersDataForCheckedParams, UsersCountParams, UsersDataParams } from "src/app/pages/admin/monitoring-details/monitoring-details.component";
import { DownloadUtil } from "../../util/download.util";
import { ApeDownloadButtonService } from "../../components/ape-download-button/ape-download-button.service";

/**
  * Parametri per la lista processi
  */
export interface ProcessListParams {
  searchedText?: string;
  fromRecord?: number;
  numRecords?: number;
  period?: string[];
  status?: string[];
}

/**
  * Servizio per gli endpoint di backoffice monitoraggio (/backoffice/monitoring/*)
  * Gestisce la gestione completa dei processi di monitoraggio
  */
@Injectable({ providedIn: "root" })
export class MonitoringBackofficeApiService extends HttpService {
  private errorHandler = inject(ErrorHandler);

  private readonly downloadService = inject(ApeDownloadButtonService);

  constructor() {
    super();
    this.logger.setCaller("MonitoringBackofficeApiService");
    this.createInstance({ url: this.env.apiBaseUrl });
  }

  /**
    * Recupera la lista dei processi
    */
  async getProcessList(params?: ProcessListParams): Promise<BackofficeProcess[]> {
    try {
      const response = await this.httpClient<BackofficeProcess[]>("/backoffice/monitoring/process/list", {
        method: API_REQUEST_METHODS.GET,
        query: params || {}
      });

      if (!response) {
        throw new Error("Invalid process list response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Conta i processi
    */
  async getProcessCount(params?: ProcessListParams): Promise<number> {
    try {
      const response = await this.httpClient<number>("/backoffice/monitoring/process/count", {
        method: API_REQUEST_METHODS.GET,
        query: params || {}
      });

      if (response === undefined || response === null) {
        throw new Error("Invalid process count response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera valori periodo disponibili
    */
  async getAvailablePeriodValues(): Promise<string[]> {
    try {
      const response = await this.httpClient<string[]>("/backoffice/process/available/values/period", { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid period values response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera valori filtri utente disponibili
    */
  async getMonitoringUserFiltersValues(processId: string): Promise<MonitoringUserFiltersValues> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<MonitoringUserFiltersValues>(`/backoffice/monitoring/user/filters/values/${processId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid user filters values response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera valori periodo disponibili
    */
  async getUsersDataCount(params?: UsersCountParams): Promise<number> {
    try {
      if (!params) {
        throw new Error("Process data is required");
      }

      const response = await this.httpClient<number>(`/backoffice/monitoring/user/count/${params.processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: params || {}
      });

      if (response === undefined || response === null) {
        throw new Error("Invalid users count response");
      }

      return response || 0;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera i dati degli utenti
    */
  async getUsersData(params?: UsersDataParams): Promise<MonitoringUserSummaryExtended[]> {
    try {
      if (!params) {
        throw new Error("Process data is required");
      }

      const response = await this.httpClient<MonitoringUserSummaryExtended[]>(`/backoffice/monitoring/user/list/${params.processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: params || {}
      });

      if (!response) {
        throw new Error("Invalid users data response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera i dati degli utenti
    */
  async getAllUsersDataForChecked(params?: AllUsersDataForCheckedParams): Promise<MonitoringUserSummaryIdsExtended[]> {
    try {
      if (!params) {
        throw new Error("Process data is required");
      }

      const response = await this.httpClient<MonitoringUserSummaryIdsExtended[]>(`/backoffice/monitoring/user/id/list/all/${params.processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: params || {}
      });

      if (!response) {
        throw new Error("Invalid users data response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera valori tipo disponibili
    */
  async getAvailableTypeValues(): Promise<string[]> {
    try {
      const response = await this.httpClient<string[]>("/backoffice/process/available/values/type", { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid type values response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Crea un nuovo processo
    */
  async createProcess(request: CreateProcessRequest): Promise<Process> {
    try {
      if (!request) {
        throw new Error("Process data is required");
      }

      const response = await this.httpClient<Process>("/backoffice/process/create", {
        method: API_REQUEST_METHODS.POST,
        body: request
      });

      if (!response) {
        throw new Error("Invalid process creation response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera dettagli di un processo
    */
  async getProcess(processId: string): Promise<BackofficeProcessDetails> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }

      const response = await this.httpClient<BackofficeProcessDetails>(`/backoffice/process/get/${processId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid process details response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera le schede di un partecipante (valutazione manager e/o autovalutazione) per la vista
    * in sola lettura dell'admin, aperta dalla voce "Visualizza schede" del monitoraggio.
    * Ogni sezione e' presente solo se la relativa fase risulta completata.
    * @param processId ID del processo
    * @param userId ID dell'utente di cui leggere le schede
    * @returns Promise con i dettagli delle schede
    */
  async getEvaluationCards(processId: string, userId: string): Promise<ManagerScorecardDetails> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }

      const response = await this.httpClient<ManagerScorecardDetails>(`/backoffice/monitoring/evaluation/cards/${processId}/${userId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid evaluation cards response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera il riepilogo di scheda di un partecipante: alimenta l'intestazione della pagina
    * "Visualizza schede" (avatar, ruolo, tipo scheda, completamento, periodo, data di condivisione).
    * @param processId ID del processo
    * @param userId ID dell'utente
    * @returns Promise con il riepilogo di scheda
    */
  async getEvaluationCardsSummary(processId: string, userId: string): Promise<ManagerScorecardSummary> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }

      const response = await this.httpClient<ManagerScorecardSummary>(`/backoffice/monitoring/evaluation/summary/${processId}/${userId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid evaluation cards summary response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera i commenti visibili su una voce dello Step 2 o 3 della scheda di un partecipante, per la
    * consultazione dalla pagina "Visualizza schede". La risposta e' sempre in sola lettura.
    * @param processId ID del processo
    * @param userId ID dell'utente proprietario della scheda
    * @param itemType Catena della voce: COMPETENCE (Step 2) o BEHAVIOUR (Step 3)
    * @param itemId ID della voce
    * @returns Promise con i commenti visibili
    */
  async getEvaluationCardsComments(processId: string, userId: string, itemType: CompetenceImportTargetEnum, itemId: string): Promise<EvaluationCommentListResult> {
    try {
      if (!processId?.trim() || !userId?.trim() || !itemId?.trim()) {
        throw new Error("Process ID, User ID and item ID are required");
      }

      const response = await this.httpClient<EvaluationCommentListResult>(`/backoffice/monitoring/evaluation/comments/${processId}/${userId}/${itemType}/${itemId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid evaluation comments response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async reopenUserEvaluation(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }

      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/evaluation/reopen/${processId}/${userId}`, { method: API_REQUEST_METHODS.POST });

      if (!response) {
        throw new Error("Invalid user evaluation reopen response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * RF-303 - Riporta in calibrazione una scheda già calibrata e non ancora condivisa al collaboratore
   */
  async reopenCalibratedScorecard(processId: string, userId: string): Promise<boolean> {
    return this.postReopen("evaluation/reopen-calibrated", processId, userId, "Invalid calibrated scorecard reopen response");
  }

  /**
   * RF-303 - Riporta allo step di Delivery una scheda già condivisa al collaboratore
   */
  async reopenSharedScorecard(processId: string, userId: string): Promise<boolean> {
    return this.postReopen("evaluation/reopen-shared", processId, userId, "Invalid shared scorecard reopen response");
  }

  private async postReopen(action: string, processId: string, userId: string, invalidResponseMessage: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/${action}/${processId}/${userId}`, { method: API_REQUEST_METHODS.POST });
      if (!response) {
        throw new Error(invalidResponseMessage);
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async reopenUserSelfEvaluation(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/selfevaluation/reopen/${processId}/${userId}`, { method: API_REQUEST_METHODS.POST });

      if (!response) {
        throw new Error("Invalid user self evaluation reopen response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Aggiorna un processo
    */
  async updateProcess(processId: string, request: BackofficeProcessUpdateRequest): Promise<Process> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!request) {
        throw new Error("Update data is required");
      }

      const response = await this.httpClient<Process>(`/backoffice/process/update/${processId}`, {
        method: API_REQUEST_METHODS.POST,
        body: request
      });

      if (!response) {
        throw new Error("Invalid process update response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera configurazione stato di un processo
    */
  async getProcessConfigurationStatus(processId: string): Promise<BackofficeProcessConfigurationStatus> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }

      const response = await this.httpClient<BackofficeProcessConfigurationStatus>(`/backoffice/process/configuration/status/${processId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid process state configuration response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera le fasi di un processo
    */
  async getProcessPhases(processId: string): Promise<BackofficeProcessPhases> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }

      const response = await this.httpClient<BackofficeProcessPhases>(`/backoffice/process/phases/get/${processId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid process phases response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Aggiorna le fasi di un processo
    */
  async updateProcessPhases(processId: string, request: BackofficeProcessPhases): Promise<BackofficeProcessPhases> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!request) {
        throw new Error("Phase data is required");
      }

      const response = await this.httpClient<BackofficeProcessPhases>(`/backoffice/process/phases/update/${processId}`, {
        method: API_REQUEST_METHODS.POST,
        body: request
      });

      if (!response) {
        throw new Error("Invalid process phases update response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Includi utenti massivamente in un processo
    */
  async includeUsersMassive(processId: string, request: ProcessUser[]): Promise<undefined> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!request) {
        throw new Error("User inclusion data is required");
      }

      await this.httpClient<undefined>(`/backoffice/process/users/include/massive/${processId}`, {
        method: API_REQUEST_METHODS.POST,
        body: request
      });
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera i dettagli di un processo specifico
    * @param processId - ID del processo
    * @param filters - Filtri opzionali per restringere le metriche
    * @returns Promise con i dettagli del processo
    * @throws {Error} Se il processId non è valido o la chiamata API fallisce
    */
  async getProcessDetails(processId: string, filters?: ProcessDetailsFilters): Promise<MonitoringSummary | MonitoringSummaryDevelopmentPlan> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }

      const query: Record<string, unknown> = { processId: processId };

      if (filters) {
        if (filters.feedback360Status?.length) {
          query.feedback360Status = filters.feedback360Status;
        }
        if (filters.selfAssessmentStatus?.length) {
          query.selfAssessmentStatus = filters.selfAssessmentStatus;
        }
        if (filters.evaluationStatus?.length) {
          query.evaluationStatus = filters.evaluationStatus;
        }
        if (filters.deliveryStatus?.length) {
          query.deliveryStatus = filters.deliveryStatus;
        }
        if (filters.developmentPlanStatus?.length) {
          query.developmentPlanStatus = filters.developmentPlanStatus;
        }
        if (filters.searchedText) {
          query.searchedText = filters.searchedText;
        }
        if (filters.office?.length) {
          query.office = filters.office;
        }
        if (filters.BU?.length) {
          query.BU = filters.BU;
        }
        if (filters.department?.length) {
          query.department = filters.department;
        }
        if (filters.company?.length) {
          query.company = filters.company;
        }
        if (filters.evaluators?.length) {
          query.evaluators = filters.evaluators;
        }
      }

      const response = await this.httpClient<MonitoringSummary>(`/backoffice/monitoring/process/summary/${processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: query
      });

      if (!response) {
        throw new Error("Invalid process details response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
     * Recupera i dettagli delle fasi di un processo specifico
     * @param processId - ID del processo
     * @returns Promise con i dettagli delle fasi del processo
     * @throws {Error} Se il processId non è valido o la chiamata API fallisce
     */
  async getProcessPhasesDetails(processId: string): Promise<BackofficeProcessPhases | null> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }

      const response = await this.httpClient<BackofficeProcessPhases>(`/backoffice/process/phases/get/${processId}`, { method: API_REQUEST_METHODS.GET });

      if (!response) {
        throw new Error("Invalid process phases details response");
      }

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async solicitUserEvaluation(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/evaluation/solicit/${processId}/${userId}`, { method: API_REQUEST_METHODS.POST });
      if (!response) {
        throw new Error("Invalid user evaluation solicit response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async solicitUserSelfEvaluation(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/selfevaluation/solicit/${processId}/${userId}`, { method: API_REQUEST_METHODS.POST });
      if (!response) {
        throw new Error("Invalid user self evaluation solicit response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async solicitAllUsersEvaluation(processId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/evaluation/massive/solicit/${processId}`, {
        method: API_REQUEST_METHODS.POST,
        body: {}
      });
      if (!response) {
        throw new Error("Invalid all users evaluation solicit response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async solicitAllUsersSelfEvaluation(processId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/selfevaluation/massive/solicit/${processId}`, {
        method: API_REQUEST_METHODS.POST,
        body: {}
      });
      if (!response) {
        throw new Error("Invalid all users self evaluation solicit response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async solicitAllUsersDevelopmentPlan(processId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/development-plan/massive/solicit/${processId}`, {
        method: API_REQUEST_METHODS.POST,
        body: {}
      });
      if (!response) {
        throw new Error("Invalid all users development plan solicit response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  async solicitUserDevelopmentPlan(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/development-plan/solicit/${processId}/${userId}`, { method: API_REQUEST_METHODS.POST });
      if (!response) {
        throw new Error("Invalid user development plan solicit response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  // =========================================================================
  // Goal Setting — solleciti monitoraggio (admin)
  // =========================================================================

  /**
   * M4: sollecita il manager del collaboratore indicato a condividere la scheda obiettivi.
   * Abilitato a UI solo per scheda in stato DA FARE in processo Attivo con fase Definizione aperta.
   */
  async solicitGoalSettingManagerForEmployee(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/goal-setting/solicit-manager/${ processId }/${ userId }`, { method: API_REQUEST_METHODS.POST });
      if (!response) {
        throw new Error("Invalid GS solicit manager response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * M3: sollecita il collaboratore indicato a inviare le proprie proposte obiettivi.
   * Abilitato a UI solo per stato proposte DA FARE su scheda Office in processo Attivo con fase Proposta aperta.
   */
  async solicitGoalSettingEmployeeProposal(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/goal-setting/solicit-employee/${ processId }/${ userId }`, { method: API_REQUEST_METHODS.POST });
      if (!response) {
        throw new Error("Invalid GS solicit employee response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * M4 massivo: sollecita tutti i manager con almeno un collaboratore Office in stato DA FARE.
   *
   * @param previewNumberOfRecipients se `true` non invia nulla e ritorna il numero di
   * destinatari che riceverebbero il sollecito. Serve alla modale di riepilogo prima
   * della conferma (spec §4.2).
   */
  async solicitAllGoalSettingManagerDefinitions(processId: string, previewNumberOfRecipients = false): Promise<number> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const url = `/backoffice/monitoring/user/goal-setting/massive/solicit-managers/${ processId }${ previewNumberOfRecipients
        ? "?previewNumberOfRecipients=true"
        : "" }`;
      const response = await this.httpClient<number>(url, { method: API_REQUEST_METHODS.POST });
      // 0 destinatari è un risultato valido: confrontiamo con undefined/null esplicitamente.
      if (response === undefined || response === null) {
        throw new Error("Invalid GS massive solicit managers response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * M3 massivo: sollecita tutti i collaboratori Office con proposte in stato DA FARE.
   *
   * @param previewNumberOfRecipients se `true` non invia nulla e ritorna il numero di
   * destinatari che riceverebbero il sollecito.
   */
  async solicitAllGoalSettingUserProposals(processId: string, previewNumberOfRecipients = false): Promise<number> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const url = `/backoffice/monitoring/user/goal-setting/massive/solicit-employees/${ processId }${ previewNumberOfRecipients
        ? "?previewNumberOfRecipients=true"
        : "" }`;
      const response = await this.httpClient<number>(url, { method: API_REQUEST_METHODS.POST });
      if (response === undefined || response === null) {
        throw new Error("Invalid GS massive solicit employees response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * M6: sollecita il manager del collaboratore indicato a completare l'aggiornamento
   * obiettivi e ricondividere la scheda. Abilitato a UI solo per processo Attivo,
   * fase Aggiornamento aperta, scheda non ancora ricondivisa (canSolicitManagerUpdate).
   */
  async solicitGoalSettingManagerUpdateForEmployee(processId: string, userId: string): Promise<boolean> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }
      const response = await this.httpClient<boolean>(`/backoffice/monitoring/user/goal-setting/solicit-manager-update/${ processId }/${ userId }`, { method: API_REQUEST_METHODS.POST });
      if (!response) {
        throw new Error("Invalid GS solicit manager update response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * M6 massivo: sollecita tutti i manager con almeno un collaboratore Office la cui
   * scheda non è ancora stata ricondivisa nella fase di Aggiornamento obiettivi.
   *
   * @param previewNumberOfRecipients se `true` non invia nulla e ritorna il numero di
   * destinatari che riceverebbero il sollecito.
   */
  async solicitAllGoalSettingManagerUpdates(processId: string, previewNumberOfRecipients = false): Promise<number> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const url = `/backoffice/monitoring/user/goal-setting/massive/solicit-managers-update/${ processId }${ previewNumberOfRecipients
        ? "?previewNumberOfRecipients=true"
        : "" }`;
      const response = await this.httpClient<number>(url, { method: API_REQUEST_METHODS.POST });
      if (response === undefined || response === null) {
        throw new Error("Invalid GS massive solicit manager updates response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Scarica la scheda di valutazione completa come PDF per l'admin
   * @param processId - ID del processo
   * @param userId - ID dell'utente
   * @returns Promise con il file PDF
   * @throws {Error} Se la chiamata API fallisce
   */
  async downloadAdminReport(processId: string, userId: string): Promise<{
    fileName: string;
    fileUrl: string;
  }> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }

      const response = await this.httpClient<{
        fileName: string;
        fileUrl: string;
      }>("/backoffice/monitoring/admin/report", {
        method: API_REQUEST_METHODS.GET,
        isFileDownload: true,
        query: {
          processId: processId,
          userId: userId
        }
      });

      if (!response?.fileUrl) {
        throw new Error("Invalid admin report download response");
      }

      return response;
    } catch (error: unknown) {
      this.logger.error("Error downloading admin report:", error as string);
      this.errorHandler.handleError(error);
      throw new Error("Failed to download admin report");
    }
  }

  /**
   * Esporta il report di processo in formato XLSX
   *
   * MO-12 - I filtri sono gli stessi della lista utenti e viaggiano in query string come per
   * `getAllUsersDataForChecked`: il file deve contenere le persone che l'amministratore ha
   * davanti al momento del click, non tutto il processo.
   *
   * @param params - Processo e filtri applicati in pagina
   * @param downloadId - ID univoco del download per gestire lo stato
   * @returns Promise con l'URL del file da scaricare
   */
  async exportProcessReport(params: AllUsersDataForCheckedParams, downloadId: string): Promise<void> {
    try {
      if (!params?.processId) {
        throw new Error("Process ID is required");
      }

      this.logger.info(`Exporting process report for process ${params.processId}`);

      // Attivare per debug modale "Download in corso..."
      // await new Promise((resolve) => setTimeout(resolve, 10000));

      const response = await this.httpClient<{
        fileName: string;
        fileUrl: string;
      }>(`/backoffice/monitoring/process-report/export/${params.processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: params,
        isFileDownload: true
      });

      if (!this.downloadService.isDownloadValid(downloadId)) {
        this.logger.info(`Download ${downloadId} was cancelled during API call, skipping file download`);
        return;
      }

      if (!response.fileName || !response.fileUrl) {
        throw new Error("Invalid process report export response");
      }

      // Genera nome file di fallback
      const fallbackFilename = `process-report_${params.processId}.xlsx`;

      // Usa l'utility per il download (form submit con target="_blank")
      DownloadUtil.downloadFromHttpServiceResponse(response, fallbackFilename);

      this.logger.info("Process report exported successfully");
    } catch (error: unknown) {
      this.logger.error("Error exporting process report:", String(error));
      this.errorHandler.handleError(error);
      throw error;
    } finally {
      this.downloadService.completeDownload(downloadId);
    }
  }
}

export type MonitoringUser = {
  id: string;
  name: string;
  email: string;
  status: string;
  selfAssessmentStatus: string;
  evaluationStatus: string;
  feedback360Status: string;
  deliveryStatus: string;
};

export type MonitoringUserSummaryExtended = MonitoringUserSummary & {
  checked: boolean;
  isVisibleActionMenu: boolean;
};

export type MonitoringUserDevelopmentPlanSummaryExtended = MonitoringUserDevelopmentPlanSummary & {
  checked: boolean;
  isVisibleActionMenu: boolean;
};

export type MonitoringUserSummaryIdsExtended = MonitoringUserSummaryIds & {
  checked: boolean;
  isVisibleActionMenu: boolean;
};

export type ProcessDetailsFilters = {
  feedback360Status?: string[];
  selfAssessmentStatus?: string[];
  evaluationStatus?: string[];
  deliveryStatus?: string[];
  developmentPlanStatus?: string[];
  searchedText?: string;
  office?: string[];
  BU?: string[];
  department?: string[];
  company?: string[];
  evaluators?: string[];
};
